import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { normalizeDocumentLogProperties } from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import DocumentLogDataGrid from "../../components/DataTables/DocumentLogDataGrid.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const DOCUMENT_TYPES = {
  0: "Полици",
  1: "Сметки",
  2: "Стикери",
  3: "Други ЦК",
  4: "СЗК",
};

const ActivityLog: React.FC = () => {
  const [formState, setFormState] = useState({
    documentType: "0",
    documentNumber: "",
    insuranceCompany: "",
    insuranceType: "",
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);

  useEffect(() => {
    setFormState({
      documentType: "0",
      documentNumber: "",
      insuranceType: "",
      insuranceCompany: "",
    });
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [companies, policyTypesRes] = await Promise.all([
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
        ]);
        setInsuranceCompanies(companies as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { documentNumber, insuranceCompany, insuranceType, documentType } =
      formState;
    if (!documentNumber) {
      setAlert("Моля, въведете номер на документа");
      setAlertType(FadeAlertType.error);
      return;
    }
    if (!insuranceCompany) {
      setAlert("Моля, изберете ЗК");
      setAlertType(FadeAlertType.error);
      return;
    }
    if (!insuranceType && documentType === "0") {
      setAlert("Моля, изберете вид полица");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      const result = await PolicyService.fetchDocumentLog(
        formState.documentNumber,
        formState.insuranceCompany,
        formState.insuranceType,
        formState.documentType,
        jwt
      );
      const normalizedLogs = Object.values(result).map((log) =>
        normalizeDocumentLogProperties(log)
      );
      setLogs(normalizedLogs);
    } catch (error) {
      setAlert(error.message);
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setAlert(null), 5000);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Движение на документи" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Движение на документи" />

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Тип документ</label>
              <SelectGroup
                value={formState.documentType}
                placeholder="Изберете тип документ"
                options={DOCUMENT_TYPES}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, documentType: value }))
                }
                name="documentType"
              />
            </div>
            <div>
              <label className={labelClass}>Номер на документ</label>
              <input
                type="text"
                name="documentNumber"
                value={formState.documentNumber}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    documentNumber: e.target.value,
                  }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.insuranceCompany}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, insuranceCompany: value }))
                }
                name="insuranceCompany"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.insuranceType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, insuranceType: value }))
                }
                name="insuranceType"
              />
            </div>
          </div>

          <div className="mt-2 flex justify-center">
            <button
              type="submit"
              className="bg-primary mt-2 w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
          </div>
        </form>

        <DocumentLogDataGrid logs={logs} isLoading={isLoading} />
      </div>
    </>
  );
};

export default ActivityLog;
